// src/hooks/useCaptures.ts - 離線優先版本 (IndexedDB + 同步佇列 + 即時監聽)
import { useState, useCallback, useEffect, useRef } from 'react';
import { Capture } from '../types/capture';
import {
  createCapture,
  getCaptures,
  getCapturesPaginated,
  updateCapture,
  deleteCapture,
  subscribeToCaptures, // 新增：即時監聽
} from '../services/api/captures';
import { eventBus, EVENTS } from '../utils/eventBus';
import {
  saveCaptures,
  getAllCaptures as getCapturesFromIndexedDB,
  cleanupExpiredCaptures,
  getCaptureCacheStats,
  syncIndexedDBWithFirestore,
  // 新增：離線優先 CRUD
  createCaptureLocal,
  updateCaptureLocal,
  deleteCaptureLocal,
  replaceTempId
} from '../services/indexedDB';
import {
  addToSyncQueue,
  processSyncQueue,
  getSyncQueueStats
} from '../services/syncQueueService';
import { logEvent } from '../utils/analytics';

// ============================================
// 單例模式管理 - 確保相同 userId 只有一個活動實例
// ============================================

interface CaptureInstanceData {
  refCount: number;
  unsubscribeFirestore?: () => void;
  unsubscribeEventBus?: () => void;
  captures: Capture[];
  isInitialized: boolean;
  cleanupTimer?: NodeJS.Timeout; // 添加延遲清理定時器
}

const captureInstances = new Map<string, CaptureInstanceData>();

const getOrCreateInstance = (userId: string): CaptureInstanceData => {
  if (!captureInstances.has(userId)) {
    captureInstances.set(userId, {
      refCount: 0,
      captures: [],
      isInitialized: false
    });
  }
  return captureInstances.get(userId)!;
};

const addInstanceRef = (userId: string): CaptureInstanceData => {
  const instance = getOrCreateInstance(userId);
  
  // 如果有待清理的定時器，取消它
  if (instance.cleanupTimer) {
    clearTimeout(instance.cleanupTimer);
    instance.cleanupTimer = undefined;
  }
  
  instance.refCount++;
  return instance;
};

const removeInstanceRef = (userId: string): boolean => {
  const instance = captureInstances.get(userId);
  if (instance) {
    instance.refCount--;
    
    if (instance.refCount <= 0) {
      
      // 使用延遲清理防止競態條件
      instance.cleanupTimer = setTimeout(() => {
        // 再次檢查引用計數，防止在延遲期間有新實例創建
        const currentInstance = captureInstances.get(userId);
        if (currentInstance && currentInstance.refCount <= 0) {
          
          // 清理訂閱
          if (currentInstance.unsubscribeFirestore) {
            currentInstance.unsubscribeFirestore();
          }
          if (currentInstance.unsubscribeEventBus) {
            currentInstance.unsubscribeEventBus();
          }
          captureInstances.delete(userId);
        } else {
        }
      }, 1000); // 1秒延遲
      
      return false; // 尚未實際清理，只是排程清理
    }
  }
  return false; // 表示還有其他引用
};

// ============================================
// Capture 管理 Hook - 支持即時監聽 + IndexedDB 快取
export const useCaptures = (userId: string) => {
  // 單例實例管理
  const instanceRef = useRef<CaptureInstanceData | null>(null);
  const isFirstInstanceRef = useRef(false);
  
  // 在 hook 初始化時添加實例引用
  if (!instanceRef.current) {
    instanceRef.current = addInstanceRef(userId);
    isFirstInstanceRef.current = instanceRef.current.refCount === 1;
  }
  
  const [captures, setCaptures] = useState<Capture[]>(() => {
    return instanceRef.current?.captures || [];
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  // 分頁相關狀態
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<unknown>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  
  // 使用 ref 追蹤狀態，避免重複請求和訂閱
  const dataLoadedRef = useRef(false);
  const subscriptionsRef = useRef<Array<() => void>>([]);
  const realtimeUnsubscribeRef = useRef<(() => void) | null>(null);
  const [useRealtime, setUseRealtime] = useState(true);
  const fallbackIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const cleanupInitializedRef = useRef(false);
  
  // 新增：網路狀態和同步相關狀態
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const lastSyncTimeRef = useRef<number | null>(null);
  
  // ============================================
  // 工具函數：智能去重機制
  // ============================================
  
  /**
   * 智能去重：基於 ID 和 content 去重，優先保留伺服器版本，防止臨時卡片重複顯示
   * @param captures Capture 陣列
   * @returns 去重後的 Capture 陣列
   */
  const deduplicateCaptures = useCallback((captures: Capture[]): Capture[] => {
    const captureMap = new Map<string, Capture>();
    const contentToIdMap = new Map<string, string>();
    
    captures.forEach(capture => {
      const existingCapture = captureMap.get(capture.id);
      
      if (!existingCapture) {
        // 檢查是否有相同 content 的其他卡片（用於處理臨時ID重複問題）
        const existingContentId = contentToIdMap.get(capture.content.trim());
        
        if (existingContentId && existingContentId !== capture.id) {
          const existingContentCapture = captureMap.get(existingContentId);
          if (existingContentCapture) {
            const isExistingTemp = existingContentCapture.id.startsWith('temp_');
            const isCurrentTemp = capture.id.startsWith('temp_');
            
            if (isExistingTemp && !isCurrentTemp) {
              // 移除臨時版本，保留真實 ID 版本
              captureMap.delete(existingContentId);
              contentToIdMap.set(capture.content.trim(), capture.id);
              captureMap.set(capture.id, capture);
            } else if (!isExistingTemp && isCurrentTemp) {
              // 已有真實 ID 版本，忽略臨時版本
              return;
            } else {
              // 都是真實 ID 或都是臨時 ID，比較時間
              const existingTime = existingContentCapture.updatedAt?.getTime() || existingContentCapture.createdAt.getTime();
              const currentTime = capture.updatedAt?.getTime() || capture.createdAt.getTime();
              
              if (currentTime > existingTime) {
                captureMap.delete(existingContentId);
                contentToIdMap.set(capture.content.trim(), capture.id);
                captureMap.set(capture.id, capture);
              }
            }
          }
        } else {
          // 首次遇到此 ID 和 content
          captureMap.set(capture.id, capture);
          contentToIdMap.set(capture.content.trim(), capture.id);
        }
      } else {
        // ID 重複，決定保留哪個版本
        // 優先級：非臨時 ID > 臨時 ID
        const isExistingTemp = existingCapture.id.startsWith('temp_');
        const isCurrentTemp = capture.id.startsWith('temp_');
        
        if (isExistingTemp && !isCurrentTemp) {
          // 現有是臨時，新的是真實 ID，替換
          captureMap.set(capture.id, capture);
          contentToIdMap.set(capture.content.trim(), capture.id);
        } else if (!isExistingTemp && isCurrentTemp) {
          // 現有是真實 ID，新的是臨時，保留現有
          // 不做操作
        } else {
          // 都是真實 ID 或都是臨時 ID，比較更新時間
          const existingTime = existingCapture.updatedAt?.getTime() || existingCapture.createdAt.getTime();
          const currentTime = capture.updatedAt?.getTime() || capture.createdAt.getTime();
          
          if (currentTime > existingTime) {
            captureMap.set(capture.id, capture);
            contentToIdMap.set(capture.content.trim(), capture.id);
          }
        }
      }
    });
    
    return Array.from(captureMap.values());
  }, []);

  // IndexedDB 存儲操作
  const saveCapturesToIndexedDB = useCallback(async (capturesToSave: Capture[]) => {
    try {
      await saveCaptures(userId, capturesToSave);
    } catch (error) {
      console.warn('保存 Capture 到 IndexedDB 失敗:', error);
    }
  }, [userId]);

  // ============================================
  // 網路狀態監聽和自動同步
  // ============================================
  
  /**
   * 網路狀態監聽效果 - 使用 debounce 防止頻繁切換
   */
  useEffect(() => {
    let onlineTimeoutId: NodeJS.Timeout | null = null;
    let offlineTimeoutId: NodeJS.Timeout | null = null;
    
    const handleOnline = async () => {
      // 清除可能存在的離線延遲處理
      if (offlineTimeoutId) {
        clearTimeout(offlineTimeoutId);
        offlineTimeoutId = null;
      }
      
      // 延遲處理，防止網路狀態快速切換
      onlineTimeoutId = setTimeout(async () => {
        setIsOnline(true);
        
        // 再延遲一小段時間確保網路穩定
        setTimeout(async () => {
          await performAutoSync();
        }, 1500);
      }, 500);
    };
    
    const handleOffline = () => {
      // 清除可能存在的上線延遲處理
      if (onlineTimeoutId) {
        clearTimeout(onlineTimeoutId);
        onlineTimeoutId = null;
      }
      
      // 延遲處理離線狀態，防止短暫的網路中斷
      offlineTimeoutId = setTimeout(() => {
        setIsOnline(false);
      }, 1000);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      // 清理定時器
      if (onlineTimeoutId) clearTimeout(onlineTimeoutId);
      if (offlineTimeoutId) clearTimeout(offlineTimeoutId);
    };
  }, []);
  
  /**
   * 執行自動同步
   */
  const performAutoSync = useCallback(async () => {
    if (isSyncing || !isOnline) return;
    
    setIsSyncing(true);
    
    try {
      const result = await processSyncQueue((processed, total) => {
      });
      
      if (result.success > 0 || result.failed > 0) {
      }
      
      lastSyncTimeRef.current = Date.now();
      
      // 同步完成後刷新本地數據
      if (result.success > 0) {
        await refreshCapturesFromServer();
      }
      
    } catch (error) {
      console.error('自動同步失敗:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, isOnline]);
  
  /**
   * 從伺服器重新整理數據（用於同步後更新）
   */
  const refreshCapturesFromServer = useCallback(async () => {
    if (!isOnline) return;
    
    try {
      const serverCaptures = await getCaptures({ userId });
      
      // 合併本地和伺服器數據，並去重
      const localCaptures = await getCapturesFromIndexedDB(userId);
      const mergedCaptures = deduplicateCaptures([...serverCaptures, ...localCaptures]);
      
      // 排序並更新狀態
      const sortedCaptures = mergedCaptures.sort((a, b) => {
        const timeA = a.createdAt;
        const timeB = b.createdAt;
        return timeB.getTime() - timeA.getTime();
      });
      
      setCaptures(sortedCaptures);

      // 同步到 IndexedDB
      await saveCapturesToIndexedDB(sortedCaptures);
      
    } catch (error) {
      console.error('從伺服器刷新數據失敗:', error);
    }
  }, [userId, isOnline, deduplicateCaptures, saveCapturesToIndexedDB]);


  // 從網路獲取更多 Capture 資料（僅用於滾動載入）
  const fetchMoreCapturesFromNetwork = useCallback(async () => {
    // 改進邏輯：如果 nextCursor 為 null，使用當前最後一個 capture 作為游標
    let cursor = nextCursor;
    if (!cursor && captures.length > 0) {
      // 使用當前顯示的最後一個 capture 的時間作為游標
      const lastCapture = captures[captures.length - 1];
      cursor = lastCapture.createdAt.getTime().toString();
    }
    
    if (!cursor) {
      return [];
    }
    
    try {
      const timestampCursor = new Date(parseInt(cursor));
      const result = await getCapturesPaginated({ 
        userId,
        limit: 20, // 載入更多時每次20筆
        startAfter: timestampCursor
      });
      
      
      // 使用函數式更新避免狀態依賴問題
      setCaptures(prev => {
        // 直接合併到現有 captures
        const newCaptures = [...prev, ...result.captures];
        
        // 去重並按創建時間排序 Capture（最新的在前）
        const sortedCaptures = deduplicateCaptures(newCaptures).sort((a, b) => {
          const timeA = a.createdAt;
          const timeB = b.createdAt;
          return timeB.getTime() - timeA.getTime();
        });

        // 異步保存到 IndexedDB
        saveCapturesToIndexedDB(sortedCaptures).catch(error => {
          console.warn('異步保存 Capture 失敗:', error);
        });
        
        return sortedCaptures;
      });
      
      // 更新分頁狀態
      setHasMore(result.hasMore);
      setLastDoc(result.lastDoc);
      setNextCursor(result.nextCursor || null);
      
      return result.captures;
    } catch (error) {
      console.error('從網路獲取更多 Capture 失敗:', error);
      throw error;
    }
  }, [userId, nextCursor, captures, deduplicateCaptures, saveCapturesToIndexedDB]);


  // 手動刷新功能 (對外接口)
  const refreshCaptures = useCallback(async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    setError(null);
    
    try {
      
      // 直接調用 getCapturesFromIndexedDB，重新載入快取數據
      const localCaptures = await getCapturesFromIndexedDB(userId);
      if (localCaptures && localCaptures.length > 0) {
        const sortedCaptures = [...localCaptures].sort((a, b) => {
          const timeA = a.createdAt;
          const timeB = b.createdAt;
          return timeB.getTime() - timeA.getTime();
        });

        setCaptures(sortedCaptures);
      }

      // 如果即時監聽被禁用，嘗試重新啟用
      if (!useRealtime) {
        setUseRealtime(true);
      }
      
    } catch (error) {
      console.error('手動刷新失敗:', error);
      setError(error instanceof Error ? error : new Error('刷新 Capture 失敗'));
    } finally {
      setIsRefreshing(false);
    }
  }, [userId, useRealtime]); // 簡化依賴，只依賴真正必要的值

  // 向後兼容的 fetchCaptures 方法
  const fetchCaptures = useCallback(async () => {
    return refreshCaptures();
  }, [refreshCaptures]);

  // 載入更多 Capture（用於無限滾動）
  const loadMoreCaptures = useCallback(async () => {
    if (isLoadingMore || !hasMore) {
      return;
    }
    
    setIsLoadingMore(true);
    setError(null);
    
    try {
      await fetchMoreCapturesFromNetwork(); // 使用新的函數
    } catch (error) {
      console.error('載入更多失敗:', error);
      setError(error instanceof Error ? error : new Error('載入更多 Capture 失敗'));
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, fetchMoreCapturesFromNetwork]);

  // 主要數據訂閱邏輯（包含初始化）
  useEffect(() => {
    if (!userId) return;

    setIsLoading(true);
    
    // 先執行初始化（如果尚未初始化）
    const initialize = async () => {
      if (!dataLoadedRef.current) {
        try {
          // 1. 從 IndexedDB 加載本地快取
          const localCaptures = await getCapturesFromIndexedDB(userId);
          if (localCaptures && localCaptures.length > 0) {
            // 直接調用狀態更新邏輯，避免依賴 handleCaptureChanges
            // 按創建時間排序 Capture（最新的在前）
            const sortedCaptures = [...localCaptures].sort((a, b) => {
              const timeA = a.createdAt;
              const timeB = b.createdAt;
              return timeB.getTime() - timeA.getTime();
            });

            setCaptures(sortedCaptures);

            /**
             * 分頁門檻判斷：當本地快取資料量達到即時監聽 limit (15) 時，
             * 假設伺服器端可能還有更多資料，顯示「載入更多」選項
             *
             * 注意：此判斷為保守策略，實際可能沒有更多資料，
             * 但避免新設備錯過載入機會（新設備只有即時監聽的 15 筆）
             */
            if (sortedCaptures.length >= 15) {
              const lastCapture = sortedCaptures[sortedCaptures.length - 1];
              setNextCursor(lastCapture.createdAt.getTime().toString());
              setHasMore(true);
            } else {
              setHasMore(false);
            }
            
          }
          
          // 2. 背景清理過期數據（非阻塞）
          if (!cleanupInitializedRef.current) {
            try {
              
              const result = await cleanupExpiredCaptures(userId, 30); // 30 天

              if (result.deletedCount > 0) {
              }
              
              cleanupInitializedRef.current = true;
            } catch (error) {
              console.warn('背景清理失敗:', error);
            }
          }
          
          dataLoadedRef.current = true;
        } catch (error) {
          console.warn('初始化 Capture 失敗:', error);
        }
      }
    };

    if (useRealtime) {
      // 嘗試即時監聽
      const startListening = async () => {
        // 先執行初始化
        await initialize();
        
        // 檢查是否已經有監聽在運行
        if (!userId || realtimeUnsubscribeRef.current) {
          setIsLoading(false);
          return;
        }
        
        try {
          
          // 只監聽最近7天的 Capture，減少即時監聽的負載
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          
          const unsubscribe = await subscribeToCaptures(userId, {
            onSnapshot: (recentUpdated) => {
              
              // 🔥 智能合併：保留完整的 IndexedDB 資料，只更新即時監聽範圍內的資料
              setCaptures(prevCaptures => {
                // 如果 prevCaptures 已經有較多資料（來自 IndexedDB），保留完整資料集
                if (prevCaptures.length > recentUpdated.length) {
                  // 只更新即時監聽範圍內的資料，保留所有其他資料
                  const updatedIds = new Set(recentUpdated.map(c => c.id));
                  
                  // 保留所有不在更新範圍的舊資料（保持完整的 IndexedDB 資料）
                  const unchangedData = prevCaptures.filter(c => !updatedIds.has(c.id));
                  
                  // 合併：即時監聽資料 + 未更新的舊資料
                  const mergedCaptures = [...recentUpdated, ...unchangedData];
                  
                  // 去重並排序
                  const sortedCaptures = deduplicateCaptures(mergedCaptures).sort((a, b) => {
                    const timeA = a.createdAt;
                    const timeB = b.createdAt;
                    return timeB.getTime() - timeA.getTime();
                  });

                  /**
                   * 分頁狀態更新（智能合併模式）
                   * 當合併後的資料量達到門檻時，假設可能還有更多歷史資料
                   */
                  if (sortedCaptures.length >= 15) {
                    const lastCapture = sortedCaptures[sortedCaptures.length - 1];
                    setNextCursor(lastCapture.createdAt.getTime().toString());
                    setHasMore(true);
                  } else {
                    setHasMore(false);
                  }

                  return sortedCaptures;
                } else {
                  // 初次載入或資料較少時，直接使用即時監聽資料
                  const sortedCaptures = deduplicateCaptures(recentUpdated).sort((a, b) => {
                    const timeA = a.createdAt;
                    const timeB = b.createdAt;
                    return timeB.getTime() - timeA.getTime();
                  });

                  /**
                   * 分頁狀態判斷（首次載入模式）
                   * 當即時監聽返回資料量達到 limit (15) 時，
                   * 推測伺服器可能還有更早的歷史資料
                   *
                   * 這是關鍵修正：新設備登入時只有即時監聽的資料，
                   * 必須正確判斷才能觸發「載入更多」
                   */
                  if (recentUpdated.length >= 15) {
                    const lastCapture = sortedCaptures[sortedCaptures.length - 1];
                    setNextCursor(lastCapture.createdAt.getTime().toString());
                    setHasMore(true);
                  } else {
                    setHasMore(false);
                  }

                  return sortedCaptures;
                }
              });
              
              // 異步更新 IndexedDB（只保存即時監聽的資料）
              saveCapturesToIndexedDB(recentUpdated).catch(error => {
                console.warn('保存即時數據到 IndexedDB 失敗:', error);
              });
            },
            onError: (error) => {
              // 檢查是否是網路連接問題
              const isNetworkError = error.message?.includes('Failed to get document') ||
                                   error.message?.includes('ERR_INTERNET_DISCONNECTED') ||
                                   error.message?.includes('ERR_NETWORK_CHANGED') ||
                                   (error as { code?: string }).code === 'unavailable';

              if (isNetworkError && !isOnline) {
                // 離線時的網路錯誤是預期的，只記錄但不設為錯誤狀態
              } else {
                console.warn('Capture 即時監聽失敗，切換到定時查詢:', error);
                setError(error);
              }
              setUseRealtime(false); // 降級到定時查詢
            }
          }, {
            limit: 15, // 監聽最近更新的15筆（優化後減少範圍提升效能）
            includeProcessed: true
          });
          
          realtimeUnsubscribeRef.current = unsubscribe;
          setError(null); // 成功連接，清除錯誤
          
        } catch (error) {
          console.error('啟動 Capture 即時監聽失敗:', error);
          setError(error instanceof Error ? error : new Error('即時監聽啟動失敗'));
          setUseRealtime(false);
        }
      };
      
      startListening().finally(() => {
        setIsLoading(false);
      });
    } else {
      // 使用定時查詢降級方案
      const startTimer = async () => {
        // 先執行初始化
        await initialize();
        
        if (fallbackIntervalRef.current) {
          setIsLoading(false);
          return;
        }
        
        
        const refreshCachesPeriodically = async () => {
          if (!document.hidden) { // 只在頁面可見時查詢
            try {
              // 直接調用 getCapturesFromIndexedDB，避免函數依賴
              const localCaptures = await getCapturesFromIndexedDB(userId);
              if (localCaptures && localCaptures.length > 0) {
                const sortedCaptures = [...localCaptures].sort((a, b) => {
                  const timeA = a.createdAt;
                  const timeB = b.createdAt;
                  return timeB.getTime() - timeA.getTime();
                });

                setCaptures(sortedCaptures);
              }
            } catch (error) {
              console.error('定時快取刷新失敗:', error);
            }
          }
        };
        
        // 立即執行一次
        refreshCachesPeriodically();
        
        // 每2分鐘查詢一次
        fallbackIntervalRef.current = setInterval(refreshCachesPeriodically, 2 * 60 * 1000);
        
        setIsLoading(false);
      };
      
      startTimer();
    }

    return () => {
      // 停止即時監聽
      if (realtimeUnsubscribeRef.current) {
        realtimeUnsubscribeRef.current();
        realtimeUnsubscribeRef.current = null;
        setError(null);
      }
      
      // 停止定時查詢
      if (fallbackIntervalRef.current) {
        clearInterval(fallbackIntervalRef.current);
        fallbackIntervalRef.current = null;
      }
    };
  }, [userId, useRealtime]); // 簡化依賴，只依賴真正必要的值

  // 訂閱 EventBus 事件 - 使用單例模式
  useEffect(() => {
    if (!userId || !instanceRef.current) return;
    
    // 只在第一個實例時執行實際訂閱
    if (isFirstInstanceRef.current && !instanceRef.current.unsubscribeEventBus) {
      
      const handleLocalEvent = () => {
        // 對於本地事件，觸發手動刷新確保數據一致性
        setIsRefreshing(currentIsRefreshing => {
          if (!currentIsRefreshing) {
            setIsRefreshing(true);
            
            // 異步執行刷新
            Promise.resolve().then(async () => {
              try {
                const localCaptures = await getCapturesFromIndexedDB(userId);
                if (localCaptures && localCaptures.length > 0) {
                  const sortedCaptures = [...localCaptures].sort((a, b) => {
                    const timeA = a.createdAt;
                    const timeB = b.createdAt;
                    return timeB.getTime() - timeA.getTime();
                  });
                  
                  // 更新共享實例的數據
                  if (instanceRef.current) {
                    instanceRef.current.captures = sortedCaptures;
                  }

                  setCaptures(sortedCaptures);
                }
              } catch (error) {
                console.error('EventBus 觸發的刷新失敗:', error);
              } finally {
                setIsRefreshing(false);
              }
            });
          }
          return currentIsRefreshing;
        });
      };
      
      const unsubscribeFunctions = [
        eventBus.subscribe(EVENTS.CAPTURE.CREATED, handleLocalEvent),
        eventBus.subscribe(EVENTS.CAPTURE.UPDATED, handleLocalEvent),
        eventBus.subscribe(EVENTS.CAPTURE.DELETED, handleLocalEvent),
        eventBus.subscribe(EVENTS.CAPTURE.CONVERTED, handleLocalEvent),
      ];
      
      // 保存清理函數到共享實例
      instanceRef.current.unsubscribeEventBus = () => {
        unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
      };
    } else if (!isFirstInstanceRef.current) {
    }
    
    return () => {
      // 個別實例清理時不做任何事，由單例管理器處理
    };
  }, [userId]);

  // 組件 unmount 時的清理邏輯 - 使用延遲清理單例模式
  useEffect(() => {
    return () => {
      
      // 減少引用計數，可能會排程延遲清理
      removeInstanceRef(userId);
      
      // 重置本地 ref 值
      dataLoadedRef.current = false;
      cleanupInitializedRef.current = false;
    };
  }, [userId]);

  // === 向後兼容的 CRUD 操作 (保持原有接口) ===

  // 創建捕捉
  const handleCreateCapture = useCallback(async (content: string, timeBoxId?: string, timeBoxTitle?: string) => {
    setIsSubmitting(true);
    
    // 生成臨時 ID
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    const now = new Date();
    
    try {
      // 創建本地 Capture 對象
      const localCapture: Capture = {
        id: tempId,
        content,
        userId,
        processed: false,
        createdAt: now,
        updatedAt: now,
        timeBoxId: timeBoxId || undefined,
        timeBoxTitle: timeBoxTitle || undefined,
      };
      
      
      // 1. 立即保存到 IndexedDB
      await createCaptureLocal(localCapture);
      
      // 2. 立即更新本地狀態（使用去重）
      setCaptures(prev => {
        const updated = deduplicateCaptures([localCapture, ...prev]);
        return updated.sort((a, b) => {
          const timeA = a.createdAt;
          const timeB = b.createdAt;
          return timeB.getTime() - timeA.getTime();
        });
      });

      // 3. 處理同步（線上/離線）
      if (isOnline) {
        try {
          
          // 準備伺服器資料
          const captureData = {
            content,
            userId,
            timeBoxId,
            timeBoxTitle
          };
          
          // 創建到伺服器
          const serverCapture = await createCapture(captureData);

          // 追蹤：創建捕捉
          try {
            logEvent('capture_created');
          } catch (error) {
            // 靜默失敗
          }


          // 替換 IndexedDB 中的臨時記錄
          await replaceTempId(tempId, serverCapture.id);
          
          // 替換本地狀態中的臨時記錄
          setCaptures(prev => {
            const withoutTemp = prev.filter(c => c.id !== tempId);
            const updated = deduplicateCaptures([serverCapture, ...withoutTemp]);
            return updated.sort((a, b) => {
              const timeA = a.createdAt;
              const timeB = b.createdAt;
              return timeB.getTime() - timeA.getTime();
            });
          });

          // 發布事件（使用真實 ID）
          eventBus.publish(EVENTS.CAPTURE.CREATED, serverCapture.id, serverCapture);
          
          return serverCapture;
          
        } catch (syncError) {
          console.warn('⚠️ 伺服器同步失敗，加入同步佇列:', syncError);
          
          // 同步失敗，加入佇列待後續處理
          await addToSyncQueue('CREATE', tempId, localCapture);
          
          // 仍返回本地版本
          return localCapture;
        }
      } else {
        
        // 離線模式：直接加入同步佇列
        await addToSyncQueue('CREATE', tempId, localCapture);
        
        return localCapture;
      }
      
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to create capture'));
      console.error('創建 Capture 失敗:', err);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, [userId, isOnline, deduplicateCaptures]);

  // 更新捕捉（離線優先）
  const handleUpdateCapture = useCallback(async (id: string, content: string, timeBoxId?: string, timeBoxTitle?: string) => {
    setIsSubmitting(true);
    
    try {
      const updateData: Partial<Capture> = {
        content,
        timeBoxId: timeBoxId || undefined,
        timeBoxTitle: timeBoxTitle || undefined,
        updatedAt: new Date(),
      };
      
      
      // 1. 立即更新 IndexedDB
      await updateCaptureLocal(id, updateData);
      
      // 2. 立即更新本地狀態
      setCaptures(prev => {
        const updated = prev.map(item => {
          if (item.id === id) {
            return { ...item, ...updateData };
          }
          return item;
        });
        return deduplicateCaptures(updated);
      });

      // 3. 處理同步（線上/離線）
      if (isOnline) {
        try {
          
          const serverUpdated = await updateCapture(id, {
            content,
            timeBoxId,
            timeBoxTitle
          });
          
          
          // 用伺服器版本更新本地狀態
          setCaptures(prev => {
            const updated = prev.map(item =>
              item.id === id ? serverUpdated : item
            );
            return deduplicateCaptures(updated);
          });

          // 發布事件
          eventBus.publish(EVENTS.CAPTURE.UPDATED, id, serverUpdated);
          
          return serverUpdated;
          
        } catch (syncError) {
          console.warn('⚠️ 伺服器更新失敗，加入同步佇列:', syncError);
          
          // 同步失敗，加入佇列
          await addToSyncQueue('UPDATE', id, updateData);
          
          // 返回本地版本
          const localUpdated = captures.find(c => c.id === id);
          return localUpdated ? { ...localUpdated, ...updateData } : null;
        }
      } else {
        
        // 離線模式：加入同步佇列
        await addToSyncQueue('UPDATE', id, updateData);
        
        const localUpdated = captures.find(c => c.id === id);
        return localUpdated ? { ...localUpdated, ...updateData } : null;
      }
      
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to update capture'));
      console.error('更新 Capture 失敗:', err);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, [isOnline, captures, deduplicateCaptures]);

  // 刪除捕捉（離線優先）
  const handleDeleteCapture = useCallback(async (id: string) => {
    try {
      
      // 1. 立即從 IndexedDB 刪除
      await deleteCaptureLocal(id);
      
      // 2. 立即從本地狀態移除
      setCaptures(prev => prev.filter(item => item.id !== id));

      // 3. 處理同步（線上/離線）
      if (isOnline) {
        try {
          
          await deleteCapture(id);
          
          
          // 發布事件
          eventBus.publish(EVENTS.CAPTURE.DELETED, id, userId);
          
        } catch (syncError) {
          console.warn('⚠️ 伺服器刪除失敗，加入同步佇列:', syncError);
          
          // 同步失敗，加入佇列
          await addToSyncQueue('DELETE', id);
        }
      } else {
        
        // 離線模式：加入同步佇列
        await addToSyncQueue('DELETE', id);
      }
      
      return true;
      
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to delete capture'));
      console.error('刪除 Capture 失敗:', err);
      return false;
    }
  }, [userId, isOnline]);

  // === 新增功能 ===

  // 獲取未處理的 Capture
  const getUnprocessedCaptures = useCallback((limit = 20) => {
    return captures
      .filter(capture => !capture.processed)
      .slice(0, limit);
  }, [captures]);

  // 新增：獲取快取統計信息（調試用）
  const getCacheStats = useCallback(async () => {
    try {
      return await getCaptureCacheStats(userId);
    } catch (error) {
      console.warn('獲取快取統計失敗:', error);
      return null;
    }
  }, [userId]);

  return {
    // === 基本狀態 ===
    captures, // 全部 Capture 數據
    isSubmitting,
    isLoading,
    error,

    // === CRUD 操作 ===
    fetchCaptures, // 手動刷新（別名）
    handleCreateCapture,
    handleUpdateCapture,
    handleDeleteCapture,

    // === 進階功能 ===
    isRefreshing,
    useRealtime, // 新增：即時監聽狀態
    refreshCaptures, // 手動刷新
    getUnprocessedCaptures, // 獲取未處理的
    getCacheStats, // 獲取快取統計
    
    // === 分頁相關接口 ===
    hasMore, // 是否還有更多數據
    isLoadingMore, // 是否正在載入更多
    loadMoreCaptures, // 載入更多函數
    nextCursor, // 下一頁游標（用於調試）
    
    // === 離線優先新增接口 ===
    isOnline, // 網路連接狀態
    isSyncing, // 是否正在同步
    performAutoSync, // 手動觸發同步
    deduplicateCaptures, // 去重函數（供外部使用）
    refreshCapturesFromServer, // 從伺服器刷新數據
    
    // === 同步佇列相關 ===
    getSyncQueueStats, // 獲取同步佇列統計
  };
};